import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  InputNumber,
  Button,
  DatePicker,
  Select,
  Space,
  Card,
  Upload,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Tag,
  Table,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  GiftOutlined,
  CheckOutlined,
  CalendarOutlined,
  TeamOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import Api from '../../Network';
import { InfiniteSelect } from '../../Component/InfiniteSelect';
import { resolveStorageUrl } from './helpers';
import './style.scss';

const { Title, Text } = Typography;

interface OfferFormParams {
  id?: string;
}

interface ProductOption {
  id: number;
  name_en?: string;
  name_ar?: string;
  picture?: string;
  selling_price?: string | number;
}

interface CustomerOption {
  id: number;
  name?: string;
  organization?: string;
  phone_number?: string;
  blocked?: boolean | number | string;
}

interface OfferLineItem {
  key: string;
  product_id: number | null;
  product?: ProductOption;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_value: number;
  total: number;
  total_after_tax: number;
}

const DEFAULT_TAX_RATE = 15;

function enrichLineItem(item: OfferLineItem): OfferLineItem {
  const qty = item.quantity || 0;
  const price = item.unit_price || 0;
  const total = qty * price;
  const taxRate = item.tax_rate ?? DEFAULT_TAX_RATE;
  const tax_value = total * (taxRate / 100);
  const total_after_tax = total + tax_value;
  return { ...item, total, tax_value, total_after_tax, tax_rate: taxRate };
}

const MAX_IMAGE_MB = 5;
const ACCEPT_IMAGE = 'image/jpeg,image/jpg,image/png';

function isCustomerBlocked(c: CustomerOption): boolean {
  return c.blocked === true || c.blocked === 1 || c.blocked === '1';
}

const OfferForm: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { id } = useParams<OfferFormParams>();
  const isEdit = Boolean(id);

  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lineItems, setLineItems] = useState<OfferLineItem[]>([]);

  const watchedSpecialPrice = Form.useWatch('special_price', form);

  const specialPriceTaxBreakdown = useMemo(() => {
    const raw = watchedSpecialPrice;
    if (raw === undefined || raw === null || raw === '') {
      return null;
    }
    const num = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
    if (!Number.isFinite(num) || num <= 0) {
      return null;
    }
    const tax = Math.round(((num * DEFAULT_TAX_RATE) / 100) * 100) / 100;
    const afterTax = Math.round((num + tax) * 100) / 100;
    return { tax, afterTax };
  }, [watchedSpecialPrice]);

  const customerLabel = (c: CustomerOption) => {
    const parts = [c.name, c.organization, c.phone_number].filter(Boolean);
    return parts.length ? parts.join(' • ') : `#${c.id}`;
  };

  const { data: customersResp, isLoading: customersLoading } = useQuery({
    queryKey: ['offers-customers-options'],
    queryFn: () =>
      Api.get('/api/admin/customers', {
        headers: { 'per-page': '1000' },
      }),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const customers: CustomerOption[] = useMemo(() => {
    const raw = (customersResp as any)?.data?.data || (customersResp as any)?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [customersResp]);

  const customersUnblocked = useMemo(
    () => customers.filter((c) => !isCustomerBlocked(c)),
    [customers]
  );

  const { data: offerResp, isLoading: offerLoading } = useQuery({
    queryKey: ['offer', id],
    queryFn: () => Api.get(`/api/admin/offers/${id}`),
    enabled: isEdit,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const offerData = (offerResp as any)?.data;

  /** On edit, include blocked customers that are already on the offer so labels stay correct. */
  const customerSelectOptions = useMemo(() => {
    const unblocked = customersUnblocked;
    if (!isEdit || !offerData?.customers?.length) {
      return unblocked;
    }
    const blockedOnOffer = (offerData.customers as CustomerOption[]).filter((c) =>
      isCustomerBlocked(c)
    );
    const merged = [...unblocked];
    for (const c of blockedOnOffer) {
      if (!merged.some((m) => m.id === c.id)) {
        merged.push(c);
      }
    }
    return merged;
  }, [customersUnblocked, isEdit, offerData]);

  useEffect(() => {
    if (isEdit) {
      return;
    }
    setLineItems([
      enrichLineItem({
        key: `new-${Date.now()}`,
        product_id: null,
        quantity: 1,
        unit_price: 0,
        tax_rate: DEFAULT_TAX_RATE,
        tax_value: 0,
        total: 0,
        total_after_tax: 0,
      }),
    ]);
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) {
      return;
    }
    const offer = offerData;
    if (!offer) {
      return;
    }
    form.setFieldsValue({
      title: offer.title || '',
      special_price: offer.special_price ? Number(offer.special_price) : undefined,
      expiry_date: offer.expiry_date ? dayjs(offer.expiry_date) : undefined,
      status: offer.status || 'Active',
      customer_ids: (offer.customers || []).map((c: CustomerOption) => c.id),
    });

    const products = offer.products || [];
    const mapped: OfferLineItem[] = products.map((p: any, idx: number) =>
      enrichLineItem({
        key: `edit-${p.id}-${idx}`,
        product_id: p.id,
        product: p as ProductOption,
        quantity: Number(p.pivot?.quantity ?? 1),
        unit_price: Number(p.pivot?.unit_price ?? p.selling_price ?? 0),
        tax_rate: DEFAULT_TAX_RATE,
        tax_value: 0,
        total: 0,
        total_after_tax: 0,
      })
    );
    setLineItems(
      mapped.length > 0
        ? mapped
        : [
            enrichLineItem({
              key: `edit-empty-${Date.now()}`,
              product_id: null,
              quantity: 1,
              unit_price: 0,
              tax_rate: DEFAULT_TAX_RATE,
              tax_value: 0,
              total: 0,
              total_after_tax: 0,
            }),
          ]
    );

    const imageUrl = resolveStorageUrl(offer.image_url || offer.image);
    if (imageUrl) {
      setFileList([
        {
          uid: '-1',
          name: 'offer-image',
          status: 'done',
          url: imageUrl,
        } as UploadFile,
      ]);
    }
  }, [offerData, isEdit, form]);

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      enrichLineItem({
        key: `line-${Date.now()}`,
        product_id: null,
        quantity: 1,
        unit_price: 0,
        tax_rate: DEFAULT_TAX_RATE,
        tax_value: 0,
        total: 0,
        total_after_tax: 0,
      }),
    ]);
  };

  const removeLineItem = (key: string) => {
    setLineItems((prev) => (prev.length <= 1 ? prev : prev.filter((item) => item.key !== key)));
  };

  const updateLineItem = (key: string, field: keyof OfferLineItem, value: unknown) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.key !== key) {
          return item;
        }
        const updated = { ...item, [field]: value } as OfferLineItem;
        return enrichLineItem(updated);
      })
    );
  };

  const handleProductSelect = (key: string, productId: number, product: ProductOption) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.key !== key) {
          return item;
        }
        let unit_price = item.unit_price;
        if (product.selling_price != null && product.selling_price !== '') {
          unit_price = parseFloat(String(product.selling_price));
        }
        return enrichLineItem({
          ...item,
          product_id: productId,
          product,
          unit_price,
        });
      })
    );
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_value || 0), 0);
    const grandTotal = lineItems.reduce((sum, item) => sum + (item.total_after_tax || 0), 0);
    return { subtotal, totalTax, grandTotal };
  };

  const buildPayload = (values: any): FormData => {
    const fd = new FormData();
    fd.append('title', String(values.title ?? '').trim());
    fd.append('special_price', String(values.special_price));
    if (values.expiry_date) {
      const d: Dayjs = values.expiry_date;
      fd.append('expiry_date', d.format('YYYY-MM-DD'));
    }
    fd.append('status', values.status || 'Active');

    const validLines = lineItems.filter((l) => l.product_id && l.quantity >= 1 && l.unit_price > 0);
    validLines.forEach((line, i) => {
      fd.append(`product_lines[${i}][product_id]`, String(line.product_id));
      fd.append(`product_lines[${i}][quantity]`, String(line.quantity));
      fd.append(`product_lines[${i}][unit_price]`, String(line.unit_price));
    });

    (values.customer_ids || []).forEach((cid: number) => fd.append('customer_ids[]', String(cid)));
    if (imageFile) {
      fd.append('image', imageFile);
    }
    if (isEdit) {
      fd.append('_method', 'PUT');
    }
    return fd;
  };

  const createMutation = useMutation({
    mutationFn: (fd: FormData) =>
      Api.post('/api/admin/offers', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: () => {
      message.success(t('offers.createSuccess', 'Offer created successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/offers'] });
      queryClient.removeQueries({ queryKey: ['/api/admin/offers'] });
      queryClient.invalidateQueries({ queryKey: ['offers-summary'] });
      history.push('/offers');
    },
    onError: (error: any) => handleApiError(error),
  });

  const updateMutation = useMutation({
    mutationFn: (fd: FormData) =>
      Api.post(`/api/admin/offers/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: () => {
      message.success(t('offers.updateSuccess', 'Offer updated successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/offers'] });
      queryClient.removeQueries({ queryKey: ['/api/admin/offers'] });
      queryClient.invalidateQueries({ queryKey: ['offers-summary'] });
      queryClient.invalidateQueries({ queryKey: ['offer', id] });
      history.push('/offers');
    },
    onError: (error: any) => handleApiError(error),
  });

  const handleApiError = (error: any) => {
    if (error?.response?.status === 422) {
      const data = error?.response?.data;
      const errors = data?.errors || data?.msg;
      if (Array.isArray(errors)) {
        message.error(errors.join(', '));
      } else if (typeof errors === 'object' && errors) {
        const firstKey = Object.keys(errors)[0];
        message.error(
          Array.isArray(errors[firstKey]) ? errors[firstKey][0] : String(errors[firstKey])
        );
      } else {
        message.error(typeof errors === 'string' ? errors : t('common.error', 'Error'));
      }
    } else {
      const msg = error?.response?.data?.msg || error?.response?.data?.message;
      message.error(msg || t('common.error', 'An unexpected error occurred'));
    }
  };

  const handleSubmit = async (values: any) => {
    if (lineItems.length === 0) {
      message.error(t('offers.form.addAtLeastOneProduct', 'Please add at least one product line.'));
      return;
    }

    const invalidItems = lineItems.filter(
      (item) => !item.product_id || item.quantity < 1 || item.unit_price <= 0
    );

    if (invalidItems.length > 0) {
      message.error(
        t(
          'offers.form.completeProductLines',
          'Please choose a product, quantity, and unit price greater than zero for every line.'
        )
      );
      return;
    }

    const productIds = lineItems.map((l) => l.product_id);
    if (new Set(productIds).size !== productIds.length) {
      message.error(t('offers.form.duplicateProduct', 'Each product can only appear once.'));
      return;
    }

    setSubmitting(true);
    try {
      const fd = buildPayload(values);
      if (isEdit) {
        await updateMutation.mutateAsync(fd);
      } else {
        await createMutation.mutateAsync(fd);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isAllowedType = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
    if (!isAllowedType) {
      message.error(t('offers.form.imageInvalidType', 'Image must be JPG or PNG.'));
      return Upload.LIST_IGNORE;
    }
    const isWithinSize = file.size / 1024 / 1024 <= MAX_IMAGE_MB;
    if (!isWithinSize) {
      message.error(
        t('offers.form.imageTooLarge', 'Image must not exceed {{size}} MB.', {
          size: MAX_IMAGE_MB,
        })
      );
      return Upload.LIST_IGNORE;
    }
    setImageFile(file);
    setFileList([
      {
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
        url: URL.createObjectURL(file),
      } as UploadFile,
    ]);
    return false;
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setFileList([]);
  };

  const validateExpiryDate = (_: unknown, value: Dayjs | undefined) => {
    if (!value) {
      return Promise.reject(new Error(t('offers.form.expiryRequired', 'Expiry date is required.')));
    }
    if (!value.isAfter(dayjs(), 'day')) {
      return Promise.reject(
        new Error(t('offers.form.expiryFuture', 'Expiry date must be in the future.'))
      );
    }
    return Promise.resolve();
  };

  const selectAllCustomers = () => {
    form.setFieldsValue({
      customer_ids: customersUnblocked.map((c) => c.id),
    });
  };

  const deselectAllCustomers = () => {
    form.setFieldsValue({ customer_ids: [] });
  };

  const isLoading = customersLoading || (isEdit && offerLoading);

  const productLineColumns = [
    {
      title: t('orders.createOrder.product', 'Product'),
      dataIndex: 'product_id',
      className: 'col-product',
      width: 300,
      'data-label': t('orders.createOrder.product', 'Product'),
      render: (_productId: number | null, record: OfferLineItem) => {
        const currentItem = lineItems.find((item) => item.key === record.key);
        if (!currentItem) {
          return null;
        }
        return (
          <div data-label={t('orders.createOrder.product', 'Product')}>
            <InfiniteSelect
              key={`offer-line-product-${currentItem.key}-${currentItem.product_id ?? 'none'}-${currentItem.product?.name_ar || currentItem.product?.name_en || ''}`}
              queryKey="api/admin/products"
              placeholder={t('orders.placeholders.selectProduct', 'Select product...')}
              showSearch
              singleMode
              valueField="id"
              searchField="name_ar,name_en"
              style={{ width: '100%' }}
              value={currentItem.product_id ?? undefined}
              initialOptions={currentItem.product ? [currentItem.product] : []}
              onChange={(productId: number, option: any) => {
                const product = (option?.item || option) as ProductOption;
                if (product && productId) {
                  handleProductSelect(currentItem.key, productId, product);
                }
              }}
              labelRender={(option: any) => {
                const product = option.item || option;
                return `${product.name_ar || product.name_en} - ${product.selling_price || 0} ${t('common.SAR')}`;
              }}
            />
            {currentItem.product && (
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  marginTop: '4px',
                }}
              >
                <Text type="secondary">
                  {currentItem.product.name_ar || currentItem.product.name_en}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders.createOrder.quantity', 'Quantity'),
      dataIndex: 'quantity',
      width: 100,
      className: 'col-qty',
      'data-label': t('orders.createOrder.quantity', 'Quantity'),
      render: (value: number, record: OfferLineItem) => (
        <div data-label={t('orders.createOrder.quantity', 'Quantity')}>
          <InputNumber
            min={1}
            value={value}
            onChange={(val) => updateLineItem(record.key, 'quantity', val || 1)}
            style={{ width: '100%' }}
          />
        </div>
      ),
    },
    {
      title: t('orders.createOrder.unitPrice', 'Unit Price'),
      dataIndex: 'unit_price',
      width: 130,
      className: 'col-price',
      'data-label': t('orders.createOrder.unitPrice', 'Unit Price'),
      render: (value: number, record: OfferLineItem) => (
        <div data-label={t('orders.createOrder.unitPrice', 'Unit Price')}>
          <InputNumber
            min={0}
            step={0.01}
            value={value}
            onChange={(val) => updateLineItem(record.key, 'unit_price', val || 0)}
            style={{ width: '100%' }}
            formatter={(v) => `${v} ${t('common.SAR')}`}
            parser={(value) => parseFloat(String(value ?? '').replace(t('common.SAR'), ''))}
          />
        </div>
      ),
    },
    {
      title: t('orders.createOrder.total', 'Total'),
      dataIndex: 'total',
      className: 'col-total',
      width: 120,
      'data-label': t('orders.createOrder.total', 'Total'),
      render: (value: number) => (
        <div data-label={t('orders.createOrder.total', 'Total')}>
          <Text strong>
            {(value || 0).toFixed(2)} {t('common.SAR')}
          </Text>
        </div>
      ),
    },
    {
      title: t('orders.createOrder.taxRate', 'Tax Rate (%)'),
      dataIndex: 'tax_rate',
      className: 'col-tax-rate',
      width: 120,
      'data-label': t('orders.createOrder.taxRate', 'Tax Rate (%)'),
      render: (value: number, record: OfferLineItem) => (
        <div data-label={t('orders.createOrder.taxRate', 'Tax Rate (%)')}>
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            value={value ?? DEFAULT_TAX_RATE}
            onChange={(val = DEFAULT_TAX_RATE) => updateLineItem(record.key, 'tax_rate', val || 0)}
            style={{ width: '100%', background: '#fafafa' }}
            disabled
            suffix="%"
          />
        </div>
      ),
    },
    {
      title: t('orders.createOrder.taxValue', 'Tax amount'),
      dataIndex: 'tax_value',
      className: 'col-tax',
      width: 140,
      'data-label': t('orders.createOrder.taxValue', 'Tax amount'),
      render: (value: number) => (
        <div data-label={t('orders.createOrder.taxValue', 'Tax amount')}>
          <Text strong>
            {(value || 0).toFixed(2)} {t('common.SAR')}
          </Text>
        </div>
      ),
    },
    {
      title: t('orders.createOrder.totalAfterTax', 'Total after tax'),
      dataIndex: 'total_after_tax',
      className: 'col-grand',
      width: 160,
      'data-label': t('orders.createOrder.totalAfterTax', 'Total after tax'),
      render: (value: number) => (
        <div data-label={t('orders.createOrder.totalAfterTax', 'Total after tax')}>
          <Text strong style={{ color: '#531dab' }}>
            {(value || 0).toFixed(2)} {t('common.SAR')}
          </Text>
        </div>
      ),
    },
    {
      title: t('common.actions', 'Actions'),
      width: 80,
      className: 'col-actions',
      'data-label': t('common.actions', 'Actions'),
      render: (_: unknown, record: OfferLineItem) => (
        <div data-label={t('common.actions', 'Actions')}>
          <Popconfirm
            title={t('orders.confirmDeleteItem', 'Are you sure you want to remove this item?')}
            onConfirm={() => removeLineItem(record.key)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            disabled={lineItems.length <= 1}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              disabled={lineItems.length <= 1}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="offers-form-page">
      <div className="offers-form-header">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          className="offers-back-btn"
          onClick={() => history.push('/offers')}
        >
          {t('offers.backToList', 'Back to offers')}
        </Button>
        <Title level={3} className="offers-form-title">
          <GiftOutlined className="offers-form-title-icon" />
          {isEdit ? t('offers.editTitle', 'Edit Offer') : t('offers.newTitle', 'New Offer')}
        </Title>
        <Text type="secondary">
          {t(
            'offers.subtitle',
            'Promote specific items to specific clients with a special price and expiry.'
          )}
        </Text>
      </div>

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: 'Active' }}
          className="offers-form"
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={9}>
              <Card
                title={
                  <Space>
                    <GiftOutlined />
                    {t('offers.form.imageTitle', 'Offer image')}
                  </Space>
                }
                className="offer-card"
              >
                <Form.Item
                  name="image"
                  className="offer-image-field"
                  extra={t('offers.form.imageHelp', 'JPG or PNG, up to {{size}} MB.', {
                    size: MAX_IMAGE_MB,
                  })}
                >
                  <Upload
                    accept={ACCEPT_IMAGE}
                    listType="picture-card"
                    fileList={fileList}
                    maxCount={1}
                    beforeUpload={beforeUpload}
                    onRemove={handleRemoveImage}
                  >
                    {fileList.length === 0 && (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>{t('offers.form.upload', 'Upload')}</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </Card>
            </Col>

            <Col xs={24} lg={15}>
              <Card
                title={
                  <Space>
                    <CheckOutlined />
                    {t('offers.form.detailsTitle', 'Offer details')}
                  </Space>
                }
                className="offer-card"
              >
                <Form.Item
                  name="title"
                  label={t('offers.form.offerTitle')}
                  rules={[
                    {
                      required: true,
                      message: t('offers.form.titleRequired'),
                    },
                    { max: 255, message: t('offers.form.titleMax') },
                  ]}
                >
                  <Input
                    showCount
                    maxLength={255}
                    placeholder={t('offers.form.titlePlaceholder')}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="special_price"
                      label={t('offers.form.specialPrice', 'Special price')}
                      rules={[
                        {
                          required: true,
                          message: t('offers.form.priceRequired', 'Special price is required.'),
                        },
                        {
                          validator: (_: unknown, value: number | null | undefined) => {
                            if (value === undefined || value === null || value > 0) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                t(
                                  'offers.form.priceGreaterThanZero',
                                  'Special price must be greater than 0.'
                                )
                              )
                            );
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        precision={2}
                        addonAfter={t('common.SAR', 'SAR')}
                        placeholder="0.00"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="expiry_date"
                      label={
                        <Space size={6}>
                          <CalendarOutlined />
                          {t('offers.form.expiryDate', 'Expiry date')}
                        </Space>
                      }
                      rules={[{ validator: validateExpiryDate }]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        disabledDate={(current) => !!current && !current.isAfter(dayjs(), 'day')}
                        placeholder={t('offers.form.pickDate', 'Pick a date')}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {specialPriceTaxBreakdown ? (
                  <div className="offer-special-price-tax-summary">
                    <Text type="secondary" className="offer-special-price-tax-summary__hint">
                      {t(
                        'offers.form.specialPriceVatHint',
                        'VAT is calculated at {{rate}}% on the special price (same as order bundle totals).',
                        { rate: DEFAULT_TAX_RATE }
                      )}
                    </Text>
                    <div className="offer-special-price-tax-summary__rows">
                      <div className="offer-special-price-tax-summary__row">
                        <span className="offer-special-price-tax-summary__label">
                          {t('offers.form.specialPriceTotalTax', 'Total tax (VAT)')}
                        </span>
                        <span className="offer-special-price-tax-summary__value offer-special-price-tax-summary__value--tabular">
                          {specialPriceTaxBreakdown.tax.toFixed(2)} {t('common.SAR')}
                        </span>
                      </div>
                      <div className="offer-special-price-tax-summary__row offer-special-price-tax-summary__row--grand">
                        <span className="offer-special-price-tax-summary__label">
                          {t('offers.form.specialPriceAfterTax', 'Total after tax')}
                        </span>
                        <span className="offer-special-price-tax-summary__value offer-special-price-tax-summary__value--grand">
                          {specialPriceTaxBreakdown.afterTax.toFixed(2)} {t('common.SAR')}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isEdit && (
                  <Form.Item name="status" label={t('offers.form.status', 'Status')}>
                    <Select
                      options={[
                        {
                          value: 'Active',
                          label: t('offers.status.active', 'Active'),
                        },
                        {
                          value: 'Inactive',
                          label: t('offers.status.inactive', 'Inactive'),
                        },
                      ]}
                    />
                  </Form.Item>
                )}

                {!isEdit && (
                  <div className="offer-default-status">
                    <Text type="secondary">
                      {t('offers.form.defaultStatusHint', 'New offers default to')}{' '}
                    </Text>
                    <Tag color="green">{t('offers.status.active', 'Active')}</Tag>
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24}>
              <Card
                title={t('offers.form.productsTitle', 'Offer Items')}
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addLineItem}
                    className="offers-products-add-btn"
                  >
                    {t('orders.createOrder.addItem', 'Add Item')}
                  </Button>
                }
                className="offers-products-card"
              >
                {lineItems.length > 0 ? (
                  <>
                    <Table
                      columns={productLineColumns}
                      dataSource={lineItems}
                      pagination={false}
                      rowKey="key"
                      size="middle"
                      tableLayout="fixed"
                      scroll={{ x: 1150 }}
                      className="offers-products-table"
                    />
                    <div className="offers-products-summary">
                      <div className="offers-products-summary__row">
                        <span className="offers-products-summary__label">
                          {t('orders.createOrder.itemsCount', 'Items Count')}
                        </span>
                        <span className="offers-products-summary__value">
                          {lineItems.length} {t('orders.createOrder.items', 'items')}
                        </span>
                      </div>
                      <div className="offers-products-summary__row">
                        <span className="offers-products-summary__label">
                          {t('orders.createOrder.subtotal', 'Subtotal')}
                        </span>
                        <span className="offers-products-summary__value offers-products-summary__value--tabular">
                          {calculateTotals().subtotal.toFixed(2)} {t('common.SAR')}
                        </span>
                      </div>
                      <div className="offers-products-summary__row">
                        <span className="offers-products-summary__label">
                          {t('orders.createOrder.totalTax', 'Total Tax')}
                        </span>
                        <span className="offers-products-summary__value offers-products-summary__value--tabular">
                          {calculateTotals().totalTax.toFixed(2)} {t('common.SAR')}
                        </span>
                      </div>
                      <div className="offers-products-summary__row offers-products-summary__row--grand">
                        <span className="offers-products-summary__label">
                          {t('orders.createOrder.grandTotal', 'Grand Total')}
                        </span>
                        <span className="offers-products-summary__value offers-products-summary__value--grand">
                          {calculateTotals().grandTotal.toFixed(2)} {t('common.SAR')}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="offers-products-empty">
                    <div className="offers-products-empty__icon">📦</div>
                    <div className="offers-products-empty__text">
                      {t(
                        'orders.createOrder.noItems',
                        'No items added yet. Click "Add Item" to start building your order.'
                      )}
                    </div>
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24}>
              <Card
                title={
                  <Space>
                    <TeamOutlined />
                    {t('offers.form.customersTitle', 'Target customers')}
                  </Space>
                }
                className="offer-card"
              >
                <Space style={{ marginBottom: 8 }} wrap>
                  <Button type="link" size="small" onClick={selectAllCustomers}>
                    {t('offers.form.selectAllCustomers')}
                  </Button>
                  <Text type="secondary">|</Text>
                  <Button type="link" size="small" onClick={deselectAllCustomers}>
                    {t('offers.form.deselectAllCustomers')}
                  </Button>
                  <Text type="secondary">({t('offers.form.unblockedOnlyHint')})</Text>
                </Space>
                <Form.Item
                  name="customer_ids"
                  rules={[
                    {
                      required: true,
                      type: 'array',
                      min: 1,
                      message: t('offers.form.customersRequired', 'Select at least one customer.'),
                    },
                  ]}
                  extra={t(
                    'offers.form.customersHelp',
                    'Only the selected customers will see this offer.'
                  )}
                >
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder={t('offers.form.selectCustomers', 'Select customers')}
                    loading={customersLoading}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label || '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={customerSelectOptions.map((c) => ({
                      value: c.id,
                      label: customerLabel(c),
                    }))}
                    maxTagCount="responsive"
                  />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <div className="offer-form-footer">
            <Space>
              <Button onClick={() => history.push('/offers')} disabled={submitting}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<CheckOutlined />}
              >
                {isEdit
                  ? t('offers.form.saveChanges', 'Save changes')
                  : t('offers.form.createOffer', 'Create offer')}
              </Button>
            </Space>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default OfferForm;
